/**
 * Schedule Overrides API - Direct Supabase fetch functions
 * Requirements: 1.4, 6.1, 6.2, 6.3, 6.4, 6.5, 7.1–7.8
 */

/// <reference lib="dom" />

import { handleApiCall } from './errors';
import { getAccessToken } from './auth-token';
import type {
  ScheduleOverride,
  CreateScheduleOverrideRequest,
  UpdateScheduleOverrideRequest,
  OverrideResolutionResult,
  ClassScheduleWithPeriods,
} from '@timetable/types';
import {
  validateOverrideConflicts,
  resolveEffectiveSchedule,
  resolveEffectiveScheduleBatch,
} from './override-resolver';

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

function authHeaders() {
  const token = getAccessToken();
  return {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function restGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${SUPABASE_URL}/rest/v1/${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), { headers: authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `GET ${path} failed`);
  }
  return res.json();
}

async function restPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Prefer': 'return=representation' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `POST ${path} failed`);
  }
  return res.json();
}

async function restPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Prefer': 'return=representation' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `PATCH ${path} failed`);
  }
  return res.json();
}

async function restDelete(path: string): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `DELETE ${path} failed`);
  }
}

// ---------------------------------------------------------------------------
// Academic year warning helper (Req 6.5)
// ---------------------------------------------------------------------------

/**
 * Returns a warning string if the override's date range falls outside the
 * active (published) academic year for the school, or null if it's within range.
 */
async function getOutOfYearWarning(
  schoolId: string,
  startDate: string,
  endDate: string,
): Promise<string | null> {
  try {
    const years = await restGet<any[]>('academic_years', {
      select: 'start_date,end_date',
      school_id: `eq.${schoolId}`,
      status: 'eq.published',
      limit: '1',
    });
    if (!years || years.length === 0) return null;
    const year = years[0];
    if (startDate < year.start_date || endDate > year.end_date) {
      return `Override dates fall outside the active academic year (${year.start_date} – ${year.end_date})`;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// CRUD functions
// ---------------------------------------------------------------------------

/** Fetch all overrides for a school ordered by start_date ASC (Req 6.3) */
export async function getScheduleOverridesDirect(schoolId: string): Promise<ScheduleOverride[]> {
  return handleApiCall(async () => {
    return restGet<ScheduleOverride[]>('schedule_overrides', {
      select: '*',
      school_id: `eq.${schoolId}`,
      order: 'start_date.asc',
    });
  });
}

/** Fetch a single override by ID */
export async function getScheduleOverrideDirect(id: string): Promise<ScheduleOverride> {
  return handleApiCall(async () => {
    const rows = await restGet<ScheduleOverride[]>('schedule_overrides', {
      select: '*',
      id: `eq.${id}`,
    });
    if (!rows || rows.length === 0) throw new Error('Schedule override not found');
    return rows[0];
  });
}

export interface CreateScheduleOverrideResponse {
  override: ScheduleOverride;
  warning?: string;
}

/**
 * Create a new schedule override.
 * Validates end_date >= start_date (Req 1.4) and runs conflict checks (Req 6.1).
 * Returns a warning if dates fall outside the active academic year (Req 6.5).
 */
export async function createScheduleOverrideDirect(
  req: CreateScheduleOverrideRequest,
): Promise<CreateScheduleOverrideResponse> {
  return handleApiCall(async () => {
    // Req 1.4: end_date >= start_date
    if (req.end_date < req.start_date) {
      throw new Error('end_date must be >= start_date');
    }

    // Req 3.7: validate working_saturday_positions for alternate_saturday overrides
    if (req.override_type === 'alternate_saturday') {
      const positions = req.working_saturday_positions;
      if (!positions || positions.length === 0) {
        throw new Error('working_saturday_positions must contain at least one value');
      }
      if (positions.some(p => p < 1 || p > 5 || !Number.isInteger(p))) {
        throw new Error('working_saturday_positions values must be integers between 1 and 5');
      }
    }

    // Req 6.1: conflict validation
    const existing = await getScheduleOverridesDirect(req.school_id);
    const conflict = validateOverrideConflicts(existing, req);
    if (!conflict.valid) {
      const err: any = new Error(conflict.message || 'Conflict with existing override');
      err.status = 409;
      throw err;
    }

    const rows = await restPost<ScheduleOverride[]>('schedule_overrides', req);
    const override = rows[0];

    const warning = await getOutOfYearWarning(req.school_id, req.start_date, req.end_date);
    return { override, ...(warning ? { warning } : {}) };
  });
}

export interface UpdateScheduleOverrideResponse {
  override: ScheduleOverride;
  warning?: string;
}

/**
 * Update an existing schedule override.
 * Re-validates all conflict rules on update (Req 6.2).
 * Returns a warning if dates fall outside the active academic year (Req 6.5).
 */
export async function updateScheduleOverrideDirect(
  id: string,
  req: UpdateScheduleOverrideRequest,
): Promise<UpdateScheduleOverrideResponse> {
  return handleApiCall(async () => {
    // Fetch current to merge for validation
    const current = await getScheduleOverrideDirect(id);

    const merged = { ...current, ...req };

    // Req 1.4: end_date >= start_date
    if (merged.end_date < merged.start_date) {
      throw new Error('end_date must be >= start_date');
    }

    // Req 3.7: validate working_saturday_positions for alternate_saturday overrides
    if (merged.override_type === 'alternate_saturday') {
      const positions = merged.working_saturday_positions;
      if (!positions || positions.length === 0) {
        throw new Error('working_saturday_positions must contain at least one value');
      }
      if (positions.some((p: number) => p < 1 || p > 5 || !Number.isInteger(p))) {
        throw new Error('working_saturday_positions values must be integers between 1 and 5');
      }
    }

    // Req 6.2: re-validate conflicts as if creating fresh (exclude self)
    const existing = await getScheduleOverridesDirect(current.school_id);
    const incomingForValidation = { ...merged, id, school_id: current.school_id };
    const conflict = validateOverrideConflicts(existing, incomingForValidation);
    if (!conflict.valid) {
      const err: any = new Error(conflict.message || 'Conflict with existing override');
      err.status = 409;
      throw err;
    }

    const rows = await restPatch<ScheduleOverride[]>(`schedule_overrides?id=eq.${id}`, req);
    const override = (rows as any[])[0] ?? await getScheduleOverrideDirect(id);

    const warning = await getOutOfYearWarning(
      current.school_id,
      merged.start_date,
      merged.end_date,
    );
    return { override, ...(warning ? { warning } : {}) };
  });
}

/** Delete a schedule override (Req 6.4) */
export async function deleteScheduleOverrideDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    await restDelete(`schedule_overrides?id=eq.${id}`);
  });
}

// ---------------------------------------------------------------------------
// Effective schedule query functions
// ---------------------------------------------------------------------------

/**
 * Fetches overrides + class default schedule, then delegates to the pure resolver.
 * Requirements: 7.1–7.5, 7.7
 */
export async function getEffectiveScheduleDirect(
  schoolId: string,
  classId: string,
  date: string,
): Promise<OverrideResolutionResult> {
  return handleApiCall(async () => {
    const [overrides, classRows] = await Promise.all([
      restGet<ScheduleOverride[]>('schedule_overrides', {
        select: '*',
        school_id: `eq.${schoolId}`,
        order: 'start_date.asc',
      }),
      restGet<any[]>('classes', {
        select: 'class_schedule_id',
        id: `eq.${classId}`,
      }),
    ]);

    const classScheduleId: string | null = classRows?.[0]?.class_schedule_id ?? null;

    let defaultSchedule: ClassScheduleWithPeriods | null = null;
    if (classScheduleId) {
      const scheduleRows = await restGet<any[]>('class_schedules', {
        select: '*,schedule_periods(*)',
        id: `eq.${classScheduleId}`,
      });
      defaultSchedule = scheduleRows?.[0] ?? null;
    }

    const result = resolveEffectiveSchedule(overrides, defaultSchedule, classId, date);

    // Hydrate alternate schedule for half_day / alternate_saturday_on results
    return hydrateResult(result, overrides);
  });
}

/**
 * Single fetch of overrides + default, delegates to resolveEffectiveScheduleBatch.
 * Requirements: 7.6, 7.8
 */
export async function getEffectiveSchedulesBatchDirect(
  schoolId: string,
  classId: string,
  startDate: string,
  endDate: string,
): Promise<Map<string, OverrideResolutionResult>> {
  return handleApiCall(async () => {
    const [overrides, classRows] = await Promise.all([
      restGet<ScheduleOverride[]>('schedule_overrides', {
        select: '*',
        school_id: `eq.${schoolId}`,
        order: 'start_date.asc',
      }),
      restGet<any[]>('classes', {
        select: 'class_schedule_id',
        id: `eq.${classId}`,
      }),
    ]);

    const classScheduleId: string | null = classRows?.[0]?.class_schedule_id ?? null;

    let defaultSchedule: ClassScheduleWithPeriods | null = null;
    if (classScheduleId) {
      const scheduleRows = await restGet<any[]>('class_schedules', {
        select: '*,schedule_periods(*)',
        id: `eq.${classScheduleId}`,
      });
      defaultSchedule = scheduleRows?.[0] ?? null;
    }

    const batchResult = resolveEffectiveScheduleBatch(
      overrides,
      defaultSchedule,
      classId,
      startDate,
      endDate,
    );

    // Hydrate alternate schedules — collect unique alternate_schedule_ids first
    const altIds = new Set<string>();
    for (const result of batchResult.values()) {
      if (result.override?.alternate_schedule_id) {
        altIds.add(result.override.alternate_schedule_id);
      }
    }

    const altScheduleMap = new Map<string, ClassScheduleWithPeriods>();
    if (altIds.size > 0) {
      const ids = [...altIds].map(id => `"${id}"`).join(',');
      const altRows = await restGet<any[]>(`class_schedules?id=in.(${ids})`, {
        select: '*,schedule_periods(*)',
      });
      for (const row of altRows) {
        altScheduleMap.set(row.id, row);
      }
    }

    // Apply hydration to each entry
    const hydrated = new Map<string, OverrideResolutionResult>();
    for (const [date, result] of batchResult.entries()) {
      if (
        result.override?.alternate_schedule_id &&
        result.schedule === null &&
        result.reason !== 'holiday' &&
        result.reason !== 'alternate_saturday_off'
      ) {
        const altSchedule = altScheduleMap.get(result.override.alternate_schedule_id) ?? null;
        hydrated.set(date, { ...result, schedule: altSchedule });
      } else {
        hydrated.set(date, result);
      }
    }

    return hydrated;
  });
}

// ---------------------------------------------------------------------------
// Internal hydration helper
// ---------------------------------------------------------------------------

/**
 * The pure resolver returns `schedule: null` for half_day / alternate_saturday_on
 * results because it has no I/O access. This helper fetches and injects the
 * actual alternate ClassScheduleWithPeriods.
 */
async function hydrateResult(
  result: OverrideResolutionResult,
  _overrides: ScheduleOverride[],
): Promise<OverrideResolutionResult> {
  if (
    result.override?.alternate_schedule_id &&
    result.schedule === null &&
    result.reason !== 'holiday' &&
    result.reason !== 'alternate_saturday_off'
  ) {
    const scheduleRows = await restGet<any[]>('class_schedules', {
      select: '*,schedule_periods(*)',
      id: `eq.${result.override.alternate_schedule_id}`,
    });
    const altSchedule: ClassScheduleWithPeriods | null = scheduleRows?.[0] ?? null;
    return { ...result, schedule: altSchedule };
  }
  return result;
}
